// LabelsModal.tsx
import React, { useState, useRef, useEffect } from "react"
import { Modal } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTag } from "@fortawesome/free-solid-svg-icons"
import LabelsModalContent from "./LabelsModalContent.component"
import { EditorRef } from "types/questionnaire.types"
import { useDraggablePanel } from "hooks/core/useDraggablePanel"

/**
 * Props interface for the LabelsModal component
 */
interface LabelsModalProps {
  /** Ref to the editor component */
  editorRef: React.RefObject<EditorRef | null>
  /** Function to save the questionnaire */
  saveQuestionnaire: () => Promise<void>
  /** Function to scroll to a specific line in the editor */
  scrollToLine?: (lineNumber: number) => void
  /** Optional label name to jump to when modal opens */
  jumpToLabel?: string | null
  /** Callback when jump is complete */
  onJumpComplete?: () => void
  /** External control for modal visibility */
  isVisible?: boolean
  /** Callback when modal visibility changes */
  onVisibilityChange?: (visible: boolean) => void
  /** Ref to the Labels button for tour */
  buttonRef?: React.RefObject<HTMLElement | null>
  /** Whether the button should be disabled */
  isDisabled?: boolean
  /** Project identifier (for fetching the datamap) */
  projectId: string
  /** Project owner */
  owner: string
  /** Currently selected variant name ("" for master) */
  currentVariant: string
}

/**
 * LabelsModal component for managing questionnaire labels and lists
 *
 * @component
 * @param props - The component props
 * @returns The rendered LabelsModal component
 */
const LabelsModal: React.FC<LabelsModalProps> = ({
  editorRef,
  saveQuestionnaire,
  scrollToLine,
  jumpToLabel,
  onJumpComplete,
  isVisible: externalIsVisible,
  onVisibilityChange,
  buttonRef,
  isDisabled = false,
  projectId,
  owner,
  currentVariant,
}) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  // Start large (~85% of the viewport) but ALWAYS within it, so the modal never
  // pushes the antd modal-wrap past the viewport (which, with mask disabled,
  // reads as the whole page overflowing). The trailing Math.min caps each
  // dimension to the available space (minus a margin) regardless of the target.
  const [modalWidth, setModalWidth] = useState<number>(() =>
    Math.min(1500, window.innerWidth - 32, Math.max(720, Math.round(window.innerWidth * 0.85)))
  )
  const [modalHeight, setModalHeight] = useState<number>(() =>
    Math.min(900, window.innerHeight - 96, Math.max(480, Math.round(window.innerHeight * 0.85)))
  )
  //eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isResizing, setIsResizing] = useState<boolean>(false)

  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel(
    { x: 0, y: 0 },
    { panelRef, bounds: true }
  )

  // When dragged, pin the modal at absolute viewport coords (antd centers it otherwise)
  const dragStyle: React.CSSProperties =
    position.x !== 0 || position.y !== 0
      ? { position: "fixed", left: position.x, top: position.y, margin: 0 }
      : {}

  // Sync external visibility control with internal state
  useEffect(() => {
    if (externalIsVisible !== undefined) {
      setIsModalVisible(externalIsVisible)
    }
  }, [externalIsVisible])

  /**
   * Handles opening the modal (for button click)
   */
  const handleOpenModal = (): void => {
    setIsModalVisible(true)
    onVisibilityChange?.(true)
  }

  /**
   * Handles closing the modal
   */
  const handleCloseModal = (): void => {
    setIsModalVisible(false)
    onVisibilityChange?.(false)
  }

  /**
   * Handles horizontal resize (right edge)
   */
  const handleHorizontalResize = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = modalWidth

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const deltaX = moveEvent.clientX - startX
      const newWidth = Math.max(600, startWidth + deltaX)
      setModalWidth(newWidth)
    }

    const handleMouseUp = (): void => {
      setIsResizing(false)
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }

    setIsResizing(true)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  /**
   * Handles vertical resize (bottom edge)
   */
  const handleVerticalResize = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    const startY = e.clientY
    const startHeight = modalHeight

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const deltaY = moveEvent.clientY - startY
      const newHeight = Math.max(400, startHeight + deltaY)
      setModalHeight(newHeight)
    }

    const handleMouseUp = (): void => {
      setIsResizing(false)
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }

    setIsResizing(true)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  /**
   * Handles corner resize (bottom-right corner)
   */
  const handleCornerResize = (e: React.MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startY = e.clientY
    const startWidth = modalWidth
    const startHeight = modalHeight

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const deltaX = moveEvent.clientX - startX
      const deltaY = moveEvent.clientY - startY
      const newWidth = Math.max(600, startWidth + deltaX)
      const newHeight = Math.max(400, startHeight + deltaY)
      setModalWidth(newWidth)
      setModalHeight(newHeight)
    }

    const handleMouseUp = (): void => {
      setIsResizing(false)
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
    }

    setIsResizing(true)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }

  return (
    <>
      <DataQButton
        ref={buttonRef as React.RefObject<HTMLButtonElement>}
        onClick={handleOpenModal}
        type="secondary"
        isDisabled={isDisabled}
      >
        Metadata Manager
        <FontAwesomeIcon icon={faTag} className="ml-2" />
      </DataQButton>
      <Modal
        title={
          <div
            className="modal-header w-full cursor-move flex items-center"
            onMouseDown={handleHeaderMouseDown}
          >
            Metadata Manager
            <FontAwesomeIcon icon={faTag} className="mx-2 text-[#36c3ed]" />
          </div>
        }
        open={isModalVisible}
        destroyOnHidden={true}
        mask={false}
        maskClosable={false}
        footer={null}
        width={modalWidth}
        style={{ top: 24 }}
        wrapClassName="pointer-events-none"
        onCancel={handleCloseModal}
        modalRender={(modal: React.ReactNode) => (
          <div
            ref={panelRef}
            className="shadow-lg rounded-lg overflow-visible relative group"
            style={{
              width: modalWidth,
              cursor: isDragging ? "grabbing" : undefined,
              ...dragStyle,
            }}
          >
              {modal}
              {/* Resize handles */}
              <div
                className="absolute top-0 right-0 h-full w-2 cursor-ew-resize z-50 opacity-0 group-hover:opacity-100 hover:bg-blue-400/20 transition-opacity"
                onMouseDown={handleHorizontalResize}
                style={{ pointerEvents: "auto" }}
              />
              <div
                className="absolute bottom-0 left-0 w-full h-2 cursor-ns-resize z-50 opacity-0 group-hover:opacity-100 hover:bg-blue-400/20 transition-opacity"
                onMouseDown={handleVerticalResize}
                style={{ pointerEvents: "auto" }}
              />
              <div
                className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-50 opacity-0 group-hover:opacity-100 hover:bg-blue-500/30 transition-opacity"
                onMouseDown={handleCornerResize}
                style={{ pointerEvents: "auto" }}
              />
          </div>
        )}
      >
        <LabelsModalContent
          editorRef={editorRef}
          saveQuestionnaire={saveQuestionnaire}
          setIsModalVisible={setIsModalVisible}
          scrollToLine={scrollToLine}
          modalHeight={modalHeight}
          jumpToLabel={jumpToLabel}
          onJumpComplete={onJumpComplete}
          projectId={projectId}
          owner={owner}
          currentVariant={currentVariant}
        />
      </Modal>
    </>
  )
}

export default LabelsModal
